import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  TextFieldProps,
  Button,
  Paper,
} from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import useArticleFieldsOptions from './useArticleFieldsOptions';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { ADD_PROJECT } from 'constants/routes';
import { FocusEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddLineIcon from 'remixicon-react/AddLineIcon';

type PostMultiSelectCustomFieldProps = {
  autoFocus?: boolean;
  field: ConfigEntryType;
  TextFieldProps?: TextFieldProps;
  showAddProject?: boolean;
};

function PostMultiSelectCustomField({
  autoFocus,
  field,
  TextFieldProps,
  showAddProject,
}: PostMultiSelectCustomFieldProps) {
  const navigate = useNavigate();
  const { singularProjectsModuleName } = useBuildWiseModules();
  const [inputValue, setInputValue] = useState('');
  const {
    control,
    formState: { isSubmitting },
    getValues,
    setValue,
  } = useFormContext();
  const value = (useWatch({
    control,
  })?.[field.entryCode] || []) as string[];
  const defaultOptions = useMemo(
    () => value?.map?.((item) => ({ label: item, value: item })) || [],
    [value],
  );
  const { options, isLoading } = useArticleFieldsOptions({
    field,
    value: defaultOptions,
  });
  const fieldValue = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [value, options],
  );

  const handleSelect = (_: unknown, values: { label: string; value: string }[]) => {
    setValue(
      field.entryCode,
      values.map((item) => item.value),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth>
            <Autocomplete
              id={field.entryCode}
              multiple
              limitTags={3}
              disableCloseOnSelect
              inputValue={inputValue}
              onInputChange={(_, value) => {
                setInputValue(value);
              }}
              onChange={handleSelect}
              options={options}
              autoHighlight
              value={fieldValue}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText={isLoading ? 'Loading...' : 'No Data Found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={autoFocus}
                  inputRef={ref}
                  placeholder={`${field.referenceObject?.placeHolder || 'Select'}`}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  {...TextFieldProps}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.label}
                    deleteIcon={<CloseLineIcon size={20} />}
                  />
                ))
              }
              onBlur={(event: FocusEvent) => {
                if (
                  event.relatedTarget?.id === 'add-project' &&
                  showAddProject &&
                  field.entryCode === 'projectId'
                ) {
                  event.stopPropagation();
                  navigate(ADD_PROJECT, {
                    state: {
                      articleFormState: getValues(),
                      projectName: inputValue,
                    },
                  });
                }
              }}
              {...(field.entryCode === 'projectId' &&
                showAddProject && {
                  PaperComponent: ({ children, ...rest }) => (
                    <Paper {...rest}>
                      {children}
                      <Button
                        id="add-project"
                        startIcon={<AddLineIcon size={20} />}
                        sx={(theme) => ({ color: theme.palette.custom.link, m: 1 })}
                      >
                        Add {singularProjectsModuleName}
                      </Button>
                    </Paper>
                  ),
                })}
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={`${field.entryCode}`}
      />
    </>
  );
}

export default PostMultiSelectCustomField;
